import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Upload, Calendar, Clock, Globe, Lock, Eye, X, Loader, FileVideo, Image } from 'lucide-react';
import './VideoUploadForm.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// Helper to get the best available thumbnail from a project
const getProjectThumbnail = (project) => {
    // Check various locations where thumbnail might be stored
    return project.thumbnail_url
        || project.data?.thumbnail
        || project.data?.selectedGameplay?.thumbnail
        || project.data?.clips?.[0]?.thumbnail
        || project.data?.video_thumbnail
        || null;
};

const ProjectGrid = ({ projects, selectedProject, onSelect }) => (
    <div className="project-grid">
        {projects.length === 0 ? (
            <div className="no-projects">
                <p>No completed projects found with video outputs.</p>
            </div>
        ) : (
            projects.map(project => {
                const thumbnailUrl = getProjectThumbnail(project);
                return (
                    <div
                        key={project.id}
                        className={`project-card ${selectedProject?.id === project.id ? 'selected' : ''}`}
                        onClick={() => onSelect(project)}
                    >
                        <div className="project-thumbnail">
                            {thumbnailUrl ? (
                                <img src={thumbnailUrl} alt={project.name} />
                            ) : (
                                <div className="placeholder-thumb">
                                    <FileVideo size={24} />
                                </div>
                            )}
                            <div className="project-duration">
                                <FileVideo size={12} />
                            </div>
                        </div>
                        <div className="project-details">
                            <h4>{project.name}</h4>
                            <span>{new Date(project.created_at).toLocaleDateString()}</span>
                        </div>
                    </div>
                );
            })
        )}
    </div>
);

const TimeSelector = ({ selectedDate, onChange }) => {
    const date = selectedDate || new Date();

    // Generate arrays
    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, ... 55
    const periods = ['AM', 'PM'];

    const currentHours = date.getHours();
    const currentMinutes = date.getMinutes();

    // Convert 24h to 12h format for selection state
    const selectedHour12 = currentHours % 12 || 12;
    const selectedPeriod = currentHours >= 12 ? 'PM' : 'AM';

    // Round minutes to nearest 5 for display selection match (optional, but good for UI)
    const roundedMinutes = Math.round(currentMinutes / 5) * 5 % 60;

    const handleHourChange = (hour) => {
        const newDate = new Date(date);
        let h = hour;
        if (selectedPeriod === 'PM' && h !== 12) h += 12;
        if (selectedPeriod === 'AM' && h === 12) h = 0;
        newDate.setHours(h);
        onChange(newDate);
    };

    const handleMinuteChange = (minute) => {
        const newDate = new Date(date);
        newDate.setMinutes(minute);
        onChange(newDate);
    };

    const handlePeriodChange = (period) => {
        const newDate = new Date(date);
        let h = newDate.getHours();
        if (period === 'AM' && h >= 12) h -= 12;
        if (period === 'PM' && h < 12) h += 12;
        newDate.setHours(h);
        onChange(newDate);
    };

    return (
        <div className="custom-time-selector">
            <div className="time-column">
                <span className="col-header">Hr</span>
                <div className="time-options">
                    {hours.map(h => (
                        <button
                            key={h}
                            type="button"
                            className={`time-btn ${h === selectedHour12 ? 'selected' : ''}`}
                            onClick={() => handleHourChange(h)}
                        >
                            {h}
                        </button>
                    ))}
                </div>
            </div>
            <div className="time-column">
                <span className="col-header">Min</span>
                <div className="time-options">
                    {minutes.map(m => (
                        <button
                            key={m}
                            type="button"
                            className={`time-btn ${m === roundedMinutes ? 'selected' : ''}`}
                            onClick={() => handleMinuteChange(m)}
                        >
                            {m.toString().padStart(2, '0')}
                        </button>
                    ))}
                </div>
            </div>
            <div className="time-column">
                <span className="col-header">--</span>
                <div className="time-options">
                    {periods.map(p => (
                        <button
                            key={p}
                            type="button"
                            className={`time-btn ${p === selectedPeriod ? 'selected' : ''}`}
                            onClick={() => handlePeriodChange(p)}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const VideoUploadForm = ({ session, isConnected, onUploadComplete, onVideoSelect, preProject }) => {
    // ... rest of component ...
    // Note: I will replace the DatePicker part in a separate hunk to keep context clean
    const [uploadMode, setUploadMode] = useState('project'); // 'file' or 'project'
    // ...

    const [isSelectionCollapsed, setIsSelectionCollapsed] = useState(false);
    const [projects, setProjects] = useState([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        tags: '',
        privacy: 'private',
        scheduleType: 'now', // 'now' or 'schedule'
        scheduledTime: null
    });

    // Existing state
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedThumbnail, setSelectedThumbnail] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');

    const fileInputRef = useRef(null);
    const thumbnailInputRef = useRef(null);

    const location = useLocation();

    // Fetch projects when mode changes to 'project'
    useEffect(() => {
        if (uploadMode === 'project' && projects.length === 0) {
            fetchProjects();
        }
    }, [uploadMode]);

    // Handle pre-selection from navigation (e.g. from Smart Clips) OR prop
    useEffect(() => {
        const projectToLoad = preProject || location.state?.preselect;

        if (projectToLoad) {
            setUploadMode('project');
            setSelectedProject(projectToLoad);
            setIsSelectionCollapsed(true); // Auto-collapse

            // Auto-fill form
            setFormData(prev => ({
                ...prev,
                title: projectToLoad.name ? projectToLoad.name.replace('Clip: ', '') : '', // Cleanup title if needed
                description: projectToLoad.data?.script || ''
            }));

            // Trigger preview
            const videoUrl = projectToLoad.output_url || projectToLoad.data?.output_url || projectToLoad.data?.video_url;

            // Handle case where we might need a signed URL (logic shared with handleProjectSelect)
            if (videoUrl && onVideoSelect) {
                onVideoSelect({ url: videoUrl, type: 'video/mp4' });
            } else if (!videoUrl && projectToLoad.data?.supabase_path) {
                // We'll let handleProjectSelect logic handle the fetching if we pass it, 
                // but since we are setting state directly here, we might need to duplicate the fetch logic 
                // or just rely on the user clicking "Change" if they really need to preview.
                // ideally we trigger the fetch:
                fetchSignedUrl(projectToLoad.data.supabase_path).then(url => {
                    if (url && onVideoSelect) onVideoSelect({ url, type: 'video/mp4' });
                });
            }

            // Clear state so it doesn't persist on refresh/navigation?
            if (location.state?.preselect) {
                window.history.replaceState({}, document.title);
            }
        }
    }, [location.state, onVideoSelect, preProject]);

    // Helper to fetch signed URL (extracted from handleProjectSelect for reuse)
    const fetchSignedUrl = async (path) => {
        try {
            const res = await fetch(`${API_BASE}/api/storage/presigned-url`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ path })
            });

            if (res.ok) {
                const data = await res.json();
                return data.url;
            }
        } catch (err) {
            console.error('Error fetching presigned URL:', err);
        }
        return null;
    };

    const fetchProjects = async () => {
        setIsLoadingProjects(true);
        try {
            // Fetch more projects - show up to 100
            const response = await fetch(`${API_BASE}/api/projects?limit=100`, {
                headers: {
                    'Authorization': `Bearer ${session.access_token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                // Filter for projects that have a valid output URL or path anywhere
                const validProjects = (data.projects || []).filter(p =>
                    p.output_url ||
                    p.data?.output_url ||
                    p.data?.video_url ||
                    p.data?.supabase_path
                );
                setProjects(validProjects);
            }
        } catch (err) {
            console.error('Failed to fetch projects', err);
        } finally {
            setIsLoadingProjects(false);
        }
    };

    const handleProjectSelect = async (project) => {
        console.log('Selected Project:', project);
        setSelectedProject(project);
        setIsSelectionCollapsed(true); // Collapse on select
        setError('');
        // Auto-fill title/desc from project
        setFormData(prev => ({
            ...prev,
            title: prev.title || project.name,
            description: prev.description || project.data?.script || ''
        }));

        // Trigger preview
        let videoUrl = project.output_url || project.data?.output_url || project.data?.video_url;

        if (!videoUrl && project.data?.supabase_path) {
            try {
                // Fetch presigned URL
                const res = await fetch(`${API_BASE}/api/storage/presigned-url`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ path: project.data.supabase_path })
                });

                if (res.ok) {
                    const data = await res.json();
                    videoUrl = data.url;
                    console.log('Fetched Signed URL:', videoUrl);
                } else {
                    console.error('Failed to get presigned URL');
                }
            } catch (err) {
                console.error('Error fetching presigned URL:', err);
            }
        }

        console.log('Derived Video URL:', videoUrl);

        if (videoUrl && onVideoSelect) {
            onVideoSelect({ url: videoUrl, type: 'video/mp4' });
        } else if (onVideoSelect) {
            console.warn('No video URL found for project');
            onVideoSelect(null);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type.startsWith('video/')) {
                setSelectedFile(file);
                setIsSelectionCollapsed(true); // Collapse on select
                setError('');
                if (!formData.title) {
                    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                    setFormData(prev => ({ ...prev, title: nameWithoutExt }));
                }

                // Trigger preview
                if (onVideoSelect) {
                    const objectUrl = URL.createObjectURL(file);
                    onVideoSelect({ url: objectUrl, type: file.type });
                }
            } else {
                setError('Please select a valid video file');
            }
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('video/')) {
            setSelectedFile(file);
            setIsSelectionCollapsed(true); // Collapse on select
            setError('');
            if (!formData.title) {
                const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                setFormData(prev => ({ ...prev, title: nameWithoutExt }));
            }
            // Trigger preview
            if (onVideoSelect) {
                const objectUrl = URL.createObjectURL(file);
                onVideoSelect({ url: objectUrl, type: file.type });
            }
        } else {
            setError('Please drop a valid video file');
        }
    };

    const handleThumbnailSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type.startsWith('image/')) {
                setSelectedThumbnail(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setThumbnailPreview(reader.result);
                };
                reader.readAsDataURL(file);
                setError('');
            } else {
                setError('Please select a valid image file (JPG, PNG)');
            }
        }
    };

    const removeThumbnail = () => {
        setSelectedThumbnail(null);
        setThumbnailPreview(null);
        if (thumbnailInputRef.current) {
            thumbnailInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation based on mode
        if (uploadMode === 'file' && (!selectedFile || !formData.title)) {
            setError('Please select a video and enter a title');
            return;
        }
        if (uploadMode === 'project' && (!selectedProject || !formData.title)) {
            setError('Please select a project and enter a title');
            return;
        }

        setUploading(true);
        setProgress(0);
        setError('');

        try {
            const endpoint = formData.scheduleType === 'schedule'
                ? `${API_BASE}/api/youtube/schedule`
                : `${API_BASE}/api/youtube/upload`;

            let xhr = new XMLHttpRequest();

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const percent = Math.round((event.loaded / event.total) * 100);
                    setProgress(percent);
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const response = JSON.parse(xhr.responseText);
                    setUploading(false);
                    setProgress(100);
                    // Reset form
                    setSelectedFile(null);
                    setSelectedProject(null);
                    setSelectedThumbnail(null);
                    setThumbnailPreview(null);
                    setFormData({
                        title: '',
                        description: '',
                        tags: '',
                        privacy: 'private',
                        scheduleType: 'now',
                        scheduledTime: null
                    });
                    setIsSelectionCollapsed(false); // Reset collapse
                    if (onUploadComplete) {
                        onUploadComplete(response);
                    }
                } else {
                    const response = JSON.parse(xhr.responseText || '{}');
                    throw new Error(response.error || 'Upload failed');
                }
            };

            xhr.onerror = () => {
                throw new Error('Network error during upload');
            };

            xhr.open('POST', endpoint);
            xhr.setRequestHeader('Authorization', `Bearer ${session.access_token}`);

            // Different payload construction based on mode
            if (uploadMode === 'project') {
                xhr.setRequestHeader('Content-Type', 'application/json');

                // Determine the correct URL or Path
                const videoUrl = selectedProject.output_url ||
                    selectedProject.data?.output_url ||
                    selectedProject.data?.video_url;

                const supabasePath = selectedProject.data?.supabase_path;

                if (!videoUrl && !supabasePath) {
                    throw new Error("Selected project has no valid video URL or path");
                }

                const payload = {
                    video_url: videoUrl,
                    supabase_path: supabasePath,
                    project_id: selectedProject.id,
                    title: formData.title,
                    description: formData.description,
                    tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
                    privacy: formData.privacy,
                    ...(formData.scheduleType === 'schedule' && {
                        scheduled_time: formData.scheduledTime ? formData.scheduledTime.toISOString() : null
                    })
                };
                xhr.send(JSON.stringify(payload));
            } else {
                // File Mode (Multipart)
                const formDataToSend = new FormData();
                formDataToSend.append('video', selectedFile);
                formDataToSend.append('title', formData.title);
                formDataToSend.append('description', formData.description);
                formDataToSend.append('tags', formData.tags);
                formDataToSend.append('privacy', formData.privacy);

                if (selectedThumbnail) {
                    formDataToSend.append('thumbnail', selectedThumbnail);
                }

                if (formData.scheduleType === 'schedule' && formData.scheduledTime) {
                    formDataToSend.append('scheduled_time', formData.scheduledTime.toISOString());
                }

                xhr.send(formDataToSend);
            }

        } catch (err) {
            setError(err.message);
            setUploading(false);
        }
    };

    if (!isConnected) {
        return (
            <div className="upload-form-disabled">
                <Lock size={32} />
                <p>Connect your YouTube channel to upload videos</p>
            </div>
        );
    }

    // Helper to switch back to expanded selection
    const handleExpandSelection = () => {
        setIsSelectionCollapsed(false);
    };

    return (
        <form className="video-upload-form" onSubmit={handleSubmit}>

            {/* Selection Area - Either Collapsed Summary or Full Selection Tabs */}
            {isSelectionCollapsed ? (
                <div className="selection-collapsed-summary">
                    <div className="summary-left">
                        {uploadMode === 'project' ? (
                            <>
                                <FileVideo size={20} className="summary-icon" />
                                <div className="summary-info">
                                    <span className="summary-label">Selected Project</span>
                                    <span className="summary-value">{selectedProject?.name}</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <Upload size={20} className="summary-icon" />
                                <div className="summary-info">
                                    <span className="summary-label">Selected File</span>
                                    <span className="summary-value">{selectedFile?.name}</span>
                                </div>
                            </>
                        )}
                    </div>
                    <button type="button" className="change-selection-btn" onClick={handleExpandSelection}>
                        Change
                    </button>
                </div>
            ) : (
                <>
                    {/* Upload Mode Tabs */}
                    <div className="upload-mode-tabs">
                        <button
                            type="button"
                            className={`mode-tab ${uploadMode === 'project' ? 'active' : ''}`}
                            onClick={() => {
                                setUploadMode('project');
                                if (onVideoSelect) onVideoSelect(null); // Clear preview when switching modes
                            }}
                        >
                            <FileVideo size={16} /> Select from Projects
                        </button>
                        <button
                            type="button"
                            className={`mode-tab ${uploadMode === 'file' ? 'active' : ''}`}
                            onClick={() => {
                                setUploadMode('file');
                                if (onVideoSelect) onVideoSelect(null); // Clear preview when switching modes
                            }}
                        >
                            <Upload size={16} /> Upload File
                        </button>
                    </div>

                    {/* Mode Content: File Upload or Project Selection */}
                    {uploadMode === 'file' ? (
                        <div
                            className={`file-drop-zone ${selectedFile ? 'has-file' : ''}`}
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept="video/*"
                                onChange={handleFileSelect}
                                hidden
                            />
                            {selectedFile ? (
                                <div className="selected-file">
                                    <FileVideo size={32} />
                                    <div className="file-info">
                                        <span className="file-name">{selectedFile.name}</span>
                                        <span className="file-size">
                                            {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="remove-file-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedFile(null);
                                            if (onVideoSelect) onVideoSelect(null);
                                        }}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ) : (
                                <div className="drop-placeholder">
                                    <Upload size={40} />
                                    <p>Drop your video here or click to browse</p>
                                    <span>MP4, MOV, AVI up to 256GB</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="project-selection-area">
                            {isLoadingProjects ? (
                                <div className="loading-projects">
                                    <Loader size={24} className="spin" />
                                    <p>Loading projects...</p>
                                </div>
                            ) : (
                                <ProjectGrid
                                    projects={projects}
                                    selectedProject={selectedProject}
                                    onSelect={handleProjectSelect}
                                />
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Video Details */}
            <div className="form-group">
                <label>Title *</label>
                <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter video title"
                    maxLength={100}
                    required
                />
            </div>

            <div className="form-group">
                <label>Description</label>
                <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Tell viewers about your video"
                    rows={4}
                    maxLength={5000}
                />
            </div>

            <div className="form-group">
                <label>Tags</label>
                <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder="gaming, tutorial, vlog (comma separated)"
                />
            </div>

            {/* Thumbnail Upload (Only show for file upload, or maybe allow for projects too?) */}
            {/* For now allowing for both, but project might have its own thumb */}
            <div className="form-group">
                <label><Image size={14} /> Thumbnail (Optional)</label>
                <div className="thumbnail-upload-area">
                    <input
                        type="file"
                        ref={thumbnailInputRef}
                        accept="image/jpeg,image/png,image/jpg"
                        onChange={handleThumbnailSelect}
                        hidden
                    />
                    {thumbnailPreview ? (
                        <div className="thumbnail-preview">
                            <img src={thumbnailPreview} alt="Thumbnail preview" />
                            <button
                                type="button"
                                className="remove-thumbnail-btn"
                                onClick={removeThumbnail}
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="thumbnail-select-btn"
                            onClick={() => thumbnailInputRef.current?.click()}
                        >
                            <Image size={20} />
                            <span>Add Thumbnail</span>
                            <small>JPG or PNG, 1280x720 recommended</small>
                        </button>
                    )}
                </div>
            </div>

            {/* Privacy & Schedule Row */}
            <div className="form-row">
                <div className="form-group">
                    <label>Privacy</label>
                    <div className="privacy-options">
                        <button
                            type="button"
                            className={`privacy-btn ${formData.privacy === 'public' ? 'active' : ''}`}
                            onClick={() => setFormData(prev => ({ ...prev, privacy: 'public' }))}
                        >
                            <Globe size={16} />
                            Public
                        </button>
                        <button
                            type="button"
                            className={`privacy-btn ${formData.privacy === 'unlisted' ? 'active' : ''}`}
                            onClick={() => setFormData(prev => ({ ...prev, privacy: 'unlisted' }))}
                        >
                            <Eye size={16} />
                            Unlisted
                        </button>
                        <button
                            type="button"
                            className={`privacy-btn ${formData.privacy === 'private' ? 'active' : ''}`}
                            onClick={() => setFormData(prev => ({ ...prev, privacy: 'private' }))}
                        >
                            <Lock size={16} />
                            Private
                        </button>
                    </div>
                </div>

                <div className="form-group">
                    <label>When to Publish</label>
                    <div className="schedule-options">
                        <button
                            type="button"
                            className={`schedule-btn ${formData.scheduleType === 'now' ? 'active' : ''}`}
                            onClick={() => setFormData(prev => ({ ...prev, scheduleType: 'now' }))}
                        >
                            <Upload size={16} />
                            Upload Now
                        </button>
                        <button
                            type="button"
                            className={`schedule-btn ${formData.scheduleType === 'schedule' ? 'active' : ''}`}
                            onClick={() => setFormData(prev => ({ ...prev, scheduleType: 'schedule' }))}
                        >
                            <Calendar size={16} />
                            Schedule
                        </button>
                    </div>
                </div>
            </div>

            {/* DateTime Picker for Schedule */}
            {formData.scheduleType === 'schedule' && (
                <div className="form-group schedule-time-group">
                    <label><Clock size={14} /> Publish Date & Time</label>
                    <div className="custom-datepicker-wrapper">
                        <DatePicker
                            selected={formData.scheduledTime}
                            onChange={(date) => setFormData(prev => ({ ...prev, scheduledTime: date }))}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            minDate={new Date()}
                            placeholderText="Select date and time"
                            className="glass-datepicker-input"
                            calendarClassName="glass-datepicker-calendar"
                            wrapperClassName="glass-datepicker-wrapper"
                            popperClassName="glass-datepicker-popper"
                            shouldCloseOnSelect={false}
                        >
                            <TimeSelector
                                selectedDate={formData.scheduledTime}
                                onChange={(date) => setFormData(prev => ({ ...prev, scheduledTime: date }))}
                            />
                        </DatePicker>
                    </div>
                    <div className="timezone-helper">
                        <Globe size={12} />
                        <span>Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="upload-error">
                    <X size={16} />
                    <span>{error}</span>
                </div>
            )}

            {/* Progress Bar */}
            {uploading && (
                <div className="upload-progress">
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progress}%` }} />
                    </div>
                    <span>{progress}% uploaded</span>
                </div>
            )}

            {/* Submit Button */}
            <button
                type="submit"
                className="submit-btn"
                disabled={uploading || (uploadMode === 'file' ? !selectedFile : !selectedProject) || !formData.title}
            >
                {uploading ? (
                    <>
                        <Loader size={18} className="spin" />
                        Uploading...
                    </>
                ) : formData.scheduleType === 'schedule' ? (
                    <>
                        <Calendar size={18} />
                        Schedule Upload
                    </>
                ) : (
                    <>
                        <Upload size={18} />
                        Upload to YouTube
                    </>
                )}
            </button>
        </form>
    );
};
